import type { ReactNode } from "react";
import { PEN_COLORS, useDrawings, type Tool } from "../player/drawings";

interface Props {
  round: number;
}

/** A rubber, seen at an angle, with its worn end darker. A glyph was being read
 *  as a backspace key, which is a different promise. */
function EraserIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden>
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
        strokeLinecap="round"
      >
        <path d="M6.2 13.2H13" />
        <path d="M2.6 9.6 8.2 4a1.4 1.4 0 0 1 2 0l2.6 2.6a1.4 1.4 0 0 1 0 2l-4.4 4.4H5.2L2.6 10.4a.6.6 0 0 1 0-.8Z" />
        <path d="m6.6 5.6 4.6 4.6" />
      </g>
    </svg>
  );
}

const TOOLS: Array<{ tool: Tool; label: string; glyph: ReactNode }> = [
  { tool: "none", label: "Sélectionner un joueur, déplacer un tracé", glyph: "⊹" },
  { tool: "pen", label: "Tracer à main levée", glyph: "✎" },
  { tool: "line", label: "Ligne droite", glyph: "╱" },
  { tool: "arrow", label: "Flèche", glyph: "➚" },
  { tool: "text", label: "Note écrite", glyph: "T" },
  { tool: "erase", label: "Gomme : effacer un tracé", glyph: <EraserIcon /> },
];

/** The drawing palette that sits over the map: what to draw with, in which
 *  colour, and how to take it back. */
export function DrawTools({ round }: Props) {
  const tool = useDrawings((s) => s.tool);
  const color = useDrawings((s) => s.color);
  const setTool = useDrawings((s) => s.setTool);
  const setColor = useDrawings((s) => s.setColor);
  const undo = useDrawings((s) => s.undo);
  const redo = useDrawings((s) => s.redo);
  const clear = useDrawings((s) => s.clear);
  const count = useDrawings((s) => s.drawings.filter((d) => d.round === round).length);
  const canUndo = useDrawings((s) => (s.history.get(round)?.past.length ?? 0) > 0);
  const canRedo = useDrawings((s) => (s.history.get(round)?.future.length ?? 0) > 0);
  const error = useDrawings((s) => s.error);
  const editable = useDrawings((s) => s.replayId !== null);

  // Through a share link the notes are there to be read, not changed.
  if (!editable) return null;

  return (
    <div className="pointer-events-auto flex flex-col items-center gap-1">
      {error && (
        <span className="rounded bg-red-500/20 px-2 py-0.5 text-[11px] text-red-300">
          {error}
        </span>
      )}
      <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/60 p-1 backdrop-blur">
        {TOOLS.map((entry) => (
          <button
            key={entry.tool}
            title={entry.label}
            onClick={() => setTool(entry.tool)}
            className={`grid h-7 w-7 place-items-center rounded text-sm transition ${
              tool === entry.tool ? "bg-white/20" : "hover:bg-white/10"
            }`}
          >
            {entry.glyph}
          </button>
        ))}

        <span className="mx-1 h-5 w-px bg-white/15" />

        {PEN_COLORS.map((swatch) => (
          <button
            key={swatch}
            title="Couleur du tracé"
            onClick={() => setColor(swatch)}
            className={`h-4 w-4 rounded-full transition ${
              color === swatch ? "ring-2 ring-white ring-offset-1 ring-offset-black" : ""
            }`}
            style={{ background: swatch }}
          />
        ))}

        <span className="mx-1 h-5 w-px bg-white/15" />

        <button
          title="Annuler"
          onClick={() => undo(round)}
          disabled={!canUndo}
          className="grid h-7 w-7 place-items-center rounded text-sm transition hover:bg-white/10 disabled:opacity-30"
        >
          ↶
        </button>
        <button
          title="Rétablir"
          onClick={() => redo(round)}
          disabled={!canRedo}
          className="grid h-7 w-7 place-items-center rounded text-sm transition hover:bg-white/10 disabled:opacity-30"
        >
          ↷
        </button>
        <button
          title="Effacer tous les tracés du round"
          onClick={() => clear(round)}
          disabled={count === 0}
          className="h-7 rounded px-2 text-[11px] transition hover:bg-white/10 disabled:opacity-30"
        >
          Tout effacer
        </button>
      </div>
    </div>
  );
}
